import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { API } from '@/api'
import type { ServerACLL3Rule } from '@/api/type-server'
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react'
import { Button } from '@/components/base/Button'
import { Divider } from '@/components/base/Divider'
import { Dropdown } from '@/components/base/Dropdown'
import { Input } from '@/components/base/Input'
import { Loading } from '@/components/base/Loading'
import { message } from '@/components/base/Message'
import { Radio } from '@/components/base/Radio'
import { Textarea } from '@/components/base/Textarea'
import { Trans } from '@/components/Trans'
import {
  getEIPACLPath,
  getEIPL3RuleCreatePath,
  getEIPL3RuleEditPath,
  getEIPListPath,
  useUnifiedContext,
} from '@/contexts/UnifiedContext'
import { ACLAction, ACLDirection } from '@/models/acl'
import { EIPBreadcrumb } from '@/pages/eip/components/EIPBreadcrumb'
import { cn } from '@/utils/style'

enum IPVersion {
  V4 = 4,
  V6 = 6,
}

const CIDR_PATTERN = /^([^/]+)(?:\/(\d+))?$/
const INTEGER_PATTERN = /^\d+$/
const IPV4_OCTET_PATTERN = /^(0|[1-9]\d{0,2})$/
const IPV6_GROUP_PATTERN = /^[0-9a-fA-F]{1,4}$/

function parseIPv4Address(addr: string): number[] | undefined {
  const octets = addr.split('.')
  if (octets.length !== 4) return undefined
  const parsed: number[] = []
  for (const o of octets) {
    if (!IPV4_OCTET_PATTERN.test(o)) return undefined
    const n = Number(o)
    if (n < 0 || n > 255) return undefined
    parsed.push(n)
  }
  return parsed
}

function parseIPv6Address(addr: string): string[] | undefined {
  const sections = addr.split('::')
  if (sections.length > 2) return undefined

  let groups: string[]
  if (sections.length === 1) {
    groups = addr.split(':')
    if (groups.length !== 8) return undefined
  } else {
    const left = sections[0] === '' ? [] : sections[0].split(':')
    const right = sections[1] === '' ? [] : sections[1].split(':')
    const missing = 8 - left.length - right.length
    if (missing < 1) return undefined
    groups = [...left, ...Array.from({ length: missing }, () => '0'), ...right]
  }

  if (groups.length !== 8 || !groups.every((g) => IPV6_GROUP_PATTERN.test(g))) return undefined
  return groups.map((g) => Number.parseInt(g, 16).toString(16))
}

function isValidIPv4CIDR(value: string): boolean {
  const match = value.match(CIDR_PATTERN)
  if (!match) return false
  const [, addr, prefix] = match
  if (!parseIPv4Address(addr)) return false
  if (prefix !== undefined) {
    if (!INTEGER_PATTERN.test(prefix)) return false
    const p = Number(prefix)
    if (p < 0 || p > 32) return false
  }
  return true
}

function ensureCIDRPrefix(value: string): string {
  const trimmed = value.trim()
  if (!trimmed || trimmed.includes('/')) return trimmed
  const isV6 = trimmed.includes(':')
  return `${trimmed}/${isV6 ? '128' : '32'}`
}

function normalizeCIDR(value: string): string {
  const trimmed = value.trim().toLowerCase()
  if (!trimmed) return ''
  const slashIdx = trimmed.indexOf('/')
  const addr = slashIdx === -1 ? trimmed : trimmed.slice(0, slashIdx)
  const prefix = slashIdx === -1 ? undefined : trimmed.slice(slashIdx + 1)
  const isV6 = addr.includes(':')
  const defaultPrefix = isV6 ? '128' : '32'
  const normalizedPrefix = prefix === undefined ? defaultPrefix : String(Number(prefix))
  if (isV6) {
    const groups = parseIPv6Address(addr)
    return `${groups ? groups.join(':') : addr}/${normalizedPrefix}`
  }
  const octets = parseIPv4Address(addr)
  return `${octets ? octets.join('.') : addr}/${normalizedPrefix}`
}

function isValidIPv6CIDR(value: string): boolean {
  const match = value.match(CIDR_PATTERN)
  if (!match) return false
  const [, addr, prefix] = match
  if (!parseIPv6Address(addr)) return false
  if (prefix !== undefined) {
    if (!INTEGER_PATTERN.test(prefix)) return false
    const p = Number(prefix)
    if (p < 0 || p > 128) return false
  }
  return true
}

interface EIPL3RuleFormPageProps {
  mode: 'create' | 'edit'
}

type LocationState = {
  rule?: ServerACLL3Rule
  template?: ServerACLL3Rule
  defaultDirection?: ACLDirection
}

export function EIPL3RuleFormPage({ mode }: EIPL3RuleFormPageProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const ctx = useUnifiedContext()
  const { eipResId, l3RuleId } = useParams<{ eipResId: string; l3RuleId?: string }>()
  const eipId = eipResId ?? ''
  const eipListPath = getEIPListPath(ctx)
  const aclPath = getEIPACLPath(ctx, eipId)
  const isEdit = mode === 'edit'
  const navState = (location.state ?? {}) as LocationState

  const backToList = () => navigate(aclPath)
  const currentPath = isEdit
    ? getEIPL3RuleEditPath(ctx, eipId, l3RuleId ?? '')
    : getEIPL3RuleCreatePath(ctx, eipId)

  const { data: acl, isLoading: aclLoading } = useQuery({
    queryKey: ['eipACL', ctx.mode, ctx.teamResID, ctx.memberResID, eipResId],
    enabled: !!eipResId,
    queryFn: async ({ signal }) => {
      const res = await API.getACLResource({ net_res_id: eipResId! }, { signal })
      if (res.code !== 0) throw new Error(res.msg)
      return res.resource
    },
  })

  const aclResID = acl?.resID

  // Edit mode: prefer rule from navigation state; otherwise look it up from the list APIs.
  const { data: fetchedRule, isLoading: ruleLoading } = useQuery({
    queryKey: ['eipACLL3Rule', ctx.mode, ctx.teamResID, ctx.memberResID, aclResID, l3RuleId],
    enabled: isEdit && !navState.rule && !!aclResID && !!l3RuleId,
    queryFn: async ({ signal }) => {
      const ruleId = Number(l3RuleId)
      const lookup = async (direction: ACLDirection) => {
        const res = await API.getACLL3Rules(
          {
            acl_res_id: aclResID!,
            traffic_direction: direction,
            page: 1,
            per_page: 10,
          },
          { signal },
        )
        if (res.code !== 0) throw new Error(res.msg)
        return (res.l3Rules ?? []).find((r) => r.id === ruleId)
      }
      const found = (await lookup(ACLDirection.Inbound)) ?? (await lookup(ACLDirection.Outbound))
      if (!found) throw new Error('rule not found')
      return found as ServerACLL3Rule
    },
  })

  const editRule = isEdit ? (navState.rule ?? fetchedRule) : undefined
  const isRuleLoading = isEdit && !navState.rule && ruleLoading

  if (aclLoading || isRuleLoading) {
    return (
      <div className="card bg-panel-white h-full flex items-center justify-center">
        <Loading />
      </div>
    )
  }

  if (!aclResID || (isEdit && !editRule)) {
    return (
      <div className="card bg-panel-white h-full overflow-y-auto">
        <FormHeader
          aclResID={acl?.resID ?? eipResId ?? ''}
          eipListPath={eipListPath}
          aclPath={aclPath}
          currentPath={currentPath}
          isEdit={isEdit}
          onBack={backToList}
        />
        <Divider />
        <div className="mt-6 t-sm text-tertiary">
          <Trans ns="eip" i18nKey="aclRules.empty">
            No ACL rules.
          </Trans>
        </div>
      </div>
    )
  }

  return (
    <FormBody
      isEdit={isEdit}
      aclResID={aclResID}
      eipListPath={eipListPath}
      aclPath={aclPath}
      currentPath={currentPath}
      rule={editRule}
      template={!isEdit ? navState.template : undefined}
      defaultDirection={!isEdit ? navState.defaultDirection : undefined}
      onBack={backToList}
    />
  )
}

function FormHeader({
  aclResID,
  eipListPath,
  aclPath,
  currentPath,
  isEdit,
  onBack,
}: {
  aclResID: string
  eipListPath: string
  aclPath: string
  currentPath: string
  isEdit: boolean
  onBack: () => void
}) {
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        className="text-tertiary cursor-pointer hover:text-primary"
        onClick={onBack}
        aria-label="back"
      >
        <ArrowLeftIcon className="w-5 h-5" />
      </button>
      <EIPBreadcrumb
        items={[
          {
            label: (
              <Trans ns="eip" i18nKey="pageTitle">
                Access Point
              </Trans>
            ),
            to: eipListPath,
          },
          { label: aclResID, to: aclPath },
          {
            label: isEdit ? (
              <Trans ns="eip" i18nKey="aclRules.formEditTitle">
                Edit Rule
              </Trans>
            ) : (
              <Trans ns="eip" i18nKey="aclRules.formNewTitle">
                New Rule
              </Trans>
            ),
            to: currentPath,
            active: true,
          },
        ]}
      />
    </div>
  )
}

function FormBody({
  isEdit,
  aclResID,
  eipListPath,
  aclPath,
  currentPath,
  rule,
  template,
  defaultDirection,
  onBack,
}: {
  isEdit: boolean
  aclResID: string
  eipListPath: string
  aclPath: string
  currentPath: string
  rule?: ServerACLL3Rule
  template?: ServerACLL3Rule
  defaultDirection?: ACLDirection
  onBack: () => void
}) {
  const { t } = useTranslation('eip')
  const ctx = useUnifiedContext()
  const queryClient = useQueryClient()

  const source = isEdit ? rule : template
  const fallbackDirection: ACLDirection = isEdit
    ? ACLDirection.Inbound
    : (defaultDirection ?? ACLDirection.Inbound)

  const [loading, setLoading] = useState(false)
  const [direction, setDirection] = useState<ACLDirection>(
    (source?.direction as ACLDirection | undefined) ?? fallbackDirection,
  )
  const [action, setAction] = useState<ACLAction>(
    (source?.defaultAction as ACLAction | undefined) ?? ACLAction.Allow,
  )
  const initialIPVersion: IPVersion =
    source?.remoteCIDR && source.remoteCIDR.includes(':') ? IPVersion.V6 : IPVersion.V4
  const [ipVersion, setIPVersion] = useState<IPVersion>(initialIPVersion)
  const [remoteCIDR, setRemoteCIDR] = useState(source?.remoteCIDR ?? '')
  const [description, setDescription] = useState(source?.description ?? '')

  const ipVersionOptions = useMemo(
    () => [
      { label: t('aclRules.ipVersionV4'), value: IPVersion.V4 },
      { label: t('aclRules.ipVersionV6'), value: IPVersion.V6 },
    ],
    [t],
  )

  const sourcePlaceholder =
    ipVersion === IPVersion.V6
      ? t('aclRules.placeholderSourceIPv6')
      : t('aclRules.placeholderSourceIPv4')

  const trimmedCIDR = remoteCIDR.trim()
  const cidrInvalid = useMemo(() => {
    if (trimmedCIDR.length === 0) return false
    return ipVersion === IPVersion.V6
      ? !isValidIPv6CIDR(trimmedCIDR)
      : !isValidIPv4CIDR(trimmedCIDR)
  }, [trimmedCIDR, ipVersion])

  const { data: existingRules } = useQuery({
    queryKey: ['aclL3Rules', ctx.mode, ctx.teamResID, ctx.memberResID, aclResID, direction, 'all'],
    enabled: !!aclResID,
    queryFn: async ({ signal }) => {
      const res = await API.getACLL3Rules(
        {
          acl_res_id: aclResID,
          traffic_direction: direction,
          page: 1,
          per_page: 50,
        },
        { signal },
      )
      if (res.code !== 0) throw new Error(res.msg)
      return res.l3Rules ?? []
    },
  })

  const editingRuleId = isEdit ? rule?.id : undefined
  const duplicateExists = useMemo(() => {
    if (trimmedCIDR.length === 0 || cidrInvalid || !existingRules) return false
    const target = normalizeCIDR(trimmedCIDR)
    return existingRules.some((r) => {
      if (editingRuleId !== undefined && r.id === editingRuleId) return false
      return normalizeCIDR(r.remoteCIDR) === target
    })
  }, [trimmedCIDR, cidrInvalid, existingRules, editingRuleId])

  const cidrHasError = cidrInvalid || duplicateExists

  const canSubmit = trimmedCIDR.length > 0 && !cidrHasError && !loading

  const onSubmit = async () => {
    setLoading(true)
    try {
      if (isEdit && rule) {
        const res = await API.updateACLL3Rule({
          acl_res_id: aclResID,
          l3_rule_id: rule.id,
          version: rule.version,
          description: description.trim(),
          remote_cidr: ensureCIDRPrefix(remoteCIDR),
          default_action: action,
        })
        if (res.code === 0) {
          message.success(t('common.message.success', { ns: 'translation' }))
          queryClient.invalidateQueries({ queryKey: ['aclL3Rules'] })
          onBack()
        }
      } else {
        const res = await API.createACLL3Rule({
          acl_res_id: aclResID,
          traffic_direction: direction,
          remote_cidr: ensureCIDRPrefix(remoteCIDR),
          default_action: action,
          description: description.trim() || undefined,
        })
        if (res.code === 0) {
          message.success(t('common.message.success', { ns: 'translation' }))
          queryClient.invalidateQueries({ queryKey: ['aclL3Rules'] })
          onBack()
        }
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="card bg-panel-white h-full overflow-y-auto">
      <FormHeader
        aclResID={aclResID}
        eipListPath={eipListPath}
        aclPath={aclPath}
        currentPath={currentPath}
        isEdit={isEdit}
        onBack={onBack}
      />
      <Divider className="mt-4 mb-8" />

      <div className="flex flex-col gap-8">
        <FieldGroup
          label={
            <Trans ns="eip" i18nKey="aclRules.fieldRuleDirection">
              Rule Direction
            </Trans>
          }
        >
          <div className="grid grid-cols-2 gap-2 w-[576px] max-w-full">
            <CardRadio
              checked={direction === ACLDirection.Inbound}
              disabled={isEdit || loading}
              onSelect={() => setDirection(ACLDirection.Inbound)}
            >
              <Trans ns="eip" i18nKey="aclRules.directionInbound">
                Inbound
              </Trans>
            </CardRadio>
            <CardRadio
              checked={direction === ACLDirection.Outbound}
              disabled={isEdit || loading}
              onSelect={() => setDirection(ACLDirection.Outbound)}
            >
              <Trans ns="eip" i18nKey="aclRules.directionOutbound">
                Outbound
              </Trans>
            </CardRadio>
          </div>
        </FieldGroup>

        <FieldGroup
          label={
            <Trans ns="eip" i18nKey="aclRules.fieldAction">
              Action
            </Trans>
          }
        >
          <div className="grid grid-cols-2 gap-2 w-[576px] max-w-full">
            <CardRadio
              checked={action === ACLAction.Allow}
              disabled={loading}
              onSelect={() => setAction(ACLAction.Allow)}
              labelClassName={action === ACLAction.Allow ? 'text-active' : undefined}
            >
              <Trans ns="eip" i18nKey="aclRules.actionAllow">
                Allow
              </Trans>
            </CardRadio>
            <CardRadio
              checked={action === ACLAction.Deny}
              disabled={loading}
              onSelect={() => setAction(ACLAction.Deny)}
              labelClassName={action === ACLAction.Deny ? 'text-error' : undefined}
            >
              <Trans ns="eip" i18nKey="aclRules.actionDeny">
                Deny
              </Trans>
            </CardRadio>
          </div>
          <div className="mt-2 t-sm text-warning whitespace-nowrap">
            <Trans ns="eip" i18nKey="aclRules.actionDefaultHint">
              When Default Policy is Deny, L4 rules under this L3 can only be Allow (whitelist
              mode). Vice versa.
            </Trans>
          </div>
        </FieldGroup>

        <div className="grid grid-cols-[120px_minmax(0,1fr)] gap-4 w-full">
          <FieldGroup
            label={
              <Trans ns="eip" i18nKey="aclRules.fieldIPVersion">
                IP Version
              </Trans>
            }
          >
            <Dropdown
              options={ipVersionOptions.map((opt) => ({ ...opt, value: String(opt.value) }))}
              value={String(ipVersion)}
              disabled
              onChange={(value) => setIPVersion(Number(value) as IPVersion)}
              className="!h-11"
            />
          </FieldGroup>
          <FieldGroup
            label={
              <Trans ns="eip" i18nKey="aclRules.fieldSourceIP">
                Source IP Address
              </Trans>
            }
          >
            <Input
              value={remoteCIDR}
              placeholder={sourcePlaceholder}
              disabled={loading}
              onChange={(e) => setRemoteCIDR(e.target.value)}
              className={cn(
                'h-11 leading-[44px] !border t-sm placeholder:text-tertiary',
                cidrHasError ? '!border-error' : 'border-neutral-200',
              )}
            />
            {cidrInvalid ? (
              <div className="mt-1 t-sm text-error">
                {ipVersion === IPVersion.V6 ? (
                  <Trans ns="eip" i18nKey="aclRules.invalidIPv6CIDR">
                    Enter a valid IPv6 address or CIDR (e.g. 2001:db8::/32).
                  </Trans>
                ) : (
                  <Trans ns="eip" i18nKey="aclRules.invalidIPv4CIDR">
                    Enter a valid IPv4 address or CIDR (e.g. 192.168.1.0/24).
                  </Trans>
                )}
              </div>
            ) : duplicateExists ? (
              <div className="mt-1 t-sm text-error">
                <Trans ns="eip" i18nKey="aclRules.errorDuplicateL3Rule">
                  An L3 rule with the same Source IP already exists. Please change the Source IP or
                  edit the existing rule.
                </Trans>
              </div>
            ) : null}
          </FieldGroup>
        </div>

        <FieldGroup
          required={false}
          label={
            <Trans ns="eip" i18nKey="aclRules.fieldDescription">
              Description
            </Trans>
          }
        >
          <Textarea
            value={description}
            disabled={loading}
            placeholder={t('aclRules.placeholderDescription')}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={100}
            className="min-h-[88px] !border border-neutral-200 t-sm placeholder:text-tertiary"
          />
        </FieldGroup>
      </div>

      <div className="flex items-center justify-end gap-4 pt-4 mt-4">
        <Button
          type="outline"
          onClick={() => {
            if (!loading) onBack()
          }}
          className="min-w-[88px]"
        >
          <Trans ns="eip" i18nKey="aclRules.cancel">
            Cancel
          </Trans>
        </Button>
        <Button type="secondary" disabled={!canSubmit} onClick={onSubmit} className="min-w-[88px]">
          {isEdit ? (
            <Trans ns="eip" i18nKey="aclRules.btnSaveL3Rule">
              Save
            </Trans>
          ) : (
            <Trans ns="eip" i18nKey="aclRules.btnCreateL3Rule">
              Create L3 Rule
            </Trans>
          )}
        </Button>
      </div>
    </div>
  )
}

function FieldGroup({
  label,
  required = true,
  children,
}: {
  label: ReactNode
  required?: boolean
  children: ReactNode
}) {
  return (
    <div>
      <div className={cn('mb-2 t-sb text-primary', required && 'text-with-star')}>{label}</div>
      {children}
    </div>
  )
}

function CardRadio({
  checked,
  disabled,
  onSelect,
  children,
  labelClassName,
}: {
  checked: boolean
  disabled?: boolean
  onSelect: () => void
  children: ReactNode
  labelClassName?: string
}) {
  return (
    <button
      type="button"
      onClick={() => {
        if (!disabled) onSelect()
      }}
      disabled={disabled}
      className={cn(
        'flex items-center gap-2 px-4 h-11 rounded-lg border transition-colors text-left bg-panel-white',
        checked ? 'border-gray300' : 'border-gray300 hover:border-neutral-300',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      {disabled ? (
        <span
          aria-hidden="true"
          className={cn(
            'w-[18px] h-[18px] rounded-full flex items-center justify-center shrink-0',
            checked ? 'text-highlight/80 bg-current' : 'border-[1.5px] border-gray300',
          )}
        >
          {checked && <span className="w-2 h-2 rounded-full bg-panel-white" />}
        </span>
      ) : (
        <Radio checked={checked} disabled={disabled} className="pointer-events-none" />
      )}
      <span className={cn('t-sm text-primary', labelClassName)}>{children}</span>
    </button>
  )
}
